import { ReactNode, useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { playPurchaseEffect, PURCHASE_EFFECT_MS, PURCHASE_EFFECTS, PURCHASE_SUCCESS_MS } from './purchaseEffects';

// This counter belongs to the live screen, so hiding/changing a product does not reset A → F.
// The input is the existing personal confirmation token, never a click or a socket notice.
export function usePurchaseSuccessEffects(confirmation: number, liveSeq?: string) {
  const cardRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const observed = useRef({ liveSeq, confirmation: 0 });
  const [sequence, setSequence] = useState({ liveSeq, count: 0, queue: [] as number[] });
  const [successRun, setSuccessRun] = useState(0);
  const run = sequence.liveSeq === liveSeq ? sequence.queue[0] || 0 : 0;

  useEffect(() => {
    if (observed.current.liveSeq !== liveSeq) {
      observed.current = { liveSeq, confirmation };
      setSequence({ liveSeq, count: 0, queue: [] });
      return;
    }
    if (!confirmation || observed.current.confirmation === confirmation) return;
    observed.current.confirmation = confirmation;
    setSequence(current => {
      const count = current.count + 1;
      return { ...current, count, queue: [...current.queue, count] };
    });
  }, [confirmation, liveSeq]);

  useEffect(() => {
    if (!run) return;
    setSuccessRun(run);
    const { current: layer } = layerRef, { current: button } = buttonRef, { current: card } = cardRef;
    const stop = layer && button && card
      ? playPurchaseEffect((run - 1) % PURCHASE_EFFECTS.length, layer, button, card)
      : undefined;
    const reset = window.setTimeout(() => setSuccessRun(0), PURCHASE_SUCCESS_MS);
    const next = window.setTimeout(() => setSequence(current => ({ ...current, queue: current.queue.slice(1) })), PURCHASE_EFFECT_MS);
    return () => {
      window.clearTimeout(reset);
      window.clearTimeout(next);
      stop?.();
    };
  }, [run, liveSeq]);

  return {
    cardRef, buttonRef, layerRef,
    active: run > 0,
    successId: run && successRun === run ? run : 0,
    effect: run ? PURCHASE_EFFECTS[(run - 1) % PURCHASE_EFFECTS.length] : undefined,
  };
}

export function PurchaseSuccessLabel({ successId, label }: { successId: number; label: ReactNode }) {
  return <>
    <span className="purchase-label purchase-label-default" aria-hidden={successId > 0}>{label}</span>
    <span key={successId} className="purchase-label purchase-label-success" aria-hidden={!successId}>
      <svg className="purchase-check" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
      득템 성공!
    </span>
    {successId > 0 && <span key={`sheen-${successId}`} className="purchase-sheen" aria-hidden="true" />}
  </>;
}

export const PurchaseEffectsLayer = styled.div.attrs({ 'aria-hidden': true, 'data-testid': 'purchase-effects' })`
  position: absolute;
  inset: 0;
  z-index: 50;
  overflow: hidden;
  pointer-events: none;
  @media (prefers-reduced-motion: reduce) { display: none; }
`;

const sheen = keyframes`to { left: 120%; }`;
const draw = keyframes`to { stroke-dashoffset: 0; }`;

export const purchaseSuccessButtonStyles = css<{ $success?: boolean }>`
  position: relative;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: linear-gradient(90deg, #10b981, #2dd4bf);
    opacity: ${({ $success }) => $success ? 1 : 0};
    transition: opacity .35s ease;
  }
  .purchase-label {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    z-index: 1;
    transition: opacity .25s ease, transform .35s cubic-bezier(.16,1,.3,1);
  }
  .purchase-label-default {
    opacity: ${({ $success }) => $success ? 0 : 1};
    transform: ${({ $success }) => $success ? 'translateY(-8px)' : 'none'};
  }
  .purchase-label-success {
    color: #fff;
    opacity: ${({ $success }) => $success ? 1 : 0};
    transform: ${({ $success }) => $success ? 'none' : 'translateY(8px)'};
  }
  .purchase-check {
    width: 16px;
    height: 16px;
    fill: none;
    stroke: #fff;
    stroke-width: 3;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 24;
    stroke-dashoffset: 24;
    ${({ $success }) => $success && css`animation: ${draw} .38s ease-out .12s forwards;`}
  }
  .purchase-sheen {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 45%;
    left: -60%;
    background: linear-gradient(100deg, transparent, rgba(255,255,255,.38), transparent);
    transform: skewX(-18deg);
    pointer-events: none;
    animation: ${sheen} .75s ease-out .08s 1;
  }
  @media (prefers-reduced-motion: reduce) {
    &::before, .purchase-label { transition: none; }
    .purchase-label { transform: none; }
    .purchase-check { animation: none; stroke-dashoffset: 0; }
    .purchase-sheen { display: none; }
  }
`;
